import { Inject } from '@angular/core';
import {
  addDoc, AggregateField, AggregateQuerySnapshot, collection, deleteDoc, doc, DocumentReference,
  Firestore,
  getCountFromServer, getDoc, getDocs, orderBy, OrderByDirection, query, setDoc, updateDoc, where
} from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { FIRESTORE_TOKEN, STORAGE_TOKEN } from './firebase.tokens';

let localCache: any = {};
export class CrudService<T> {
  collectionName = 'collection';

  constructor(
    @Inject(FIRESTORE_TOKEN) protected firestore: Firestore,
    @Inject(STORAGE_TOKEN) protected storage: FirebaseStorage
  ) { }

  async getItems(): Promise<T[]> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const cacheName = `get-items-${this.collectionName}`;

    if (localCache[cacheName]) {
      return localCache[cacheName];
    }

    const snapshot = await getDocs(itemCollection);
    const items = snapshot.docs.map((docSnap) => ({
      ...(docSnap.data() as T),
      id: docSnap.id
    }));

    localCache[cacheName] = items;
    return items;
  }

  async countItems(): Promise<AggregateQuerySnapshot<{ count: AggregateField<number> }>> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const query1 = query(itemCollection);
    const cacheName = `count-items-${this.collectionName}`;

    if (localCache[cacheName]) {
      return localCache[cacheName];
    }

    const result = await getCountFromServer(query1);
    localCache[cacheName] = result;
    return result;
  }

  async countItemsFiltered(
    queryField: string,
    queryOperator: any,
    queryValue: any
  ): Promise<AggregateQuerySnapshot<{ count: AggregateField<number> }>> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const cacheName = `count-items-filtered-${this.collectionName}-${queryField}-${queryOperator}-${queryValue}`;

    if (localCache[cacheName]) {
      return localCache[cacheName];
    }

    const filteredQuery = query(itemCollection, where(queryField, queryOperator, queryValue));
    const result = await getCountFromServer(filteredQuery);

    localCache[cacheName] = result;
    return result;
  }
  async getItemsQuery(queryField: string, queryOperator: any, queryValue: any): Promise<T[]> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const cacheName = `get-items-query-${this.collectionName}-${queryField}-${queryOperator}-${queryValue}`;

    if (localCache[cacheName]) {
      return localCache[cacheName];
    }

    const myQuery = query(itemCollection, where(queryField, queryOperator, queryValue));
    const snapshot = await getDocs(myQuery);

    const result = snapshot.docs.map((docSnap) => ({
      ...(docSnap.data() as T),
      id: docSnap.id
    }));

    localCache[cacheName] = result;
    return result;
  }

  async getItemsQueries(conditions: [string, any][]): Promise<T[]> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const whereConditions = conditions.map(([field, value]) => where(field, '==', value));

    const myQuery = query(itemCollection, ...whereConditions);
    const snapshot = await getDocs(myQuery);

    return snapshot.docs.map((docSnap) => ({
      ...(docSnap.data() as T),
      id: docSnap.id
    }));
  }

  async getItemsSorted(sortColumn: string, sortOrder: string = 'asc'): Promise<T[]> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const sort: OrderByDirection = sortOrder as OrderByDirection;
    const cacheName = `get-items-sorted-${this.collectionName}-${sortColumn}-${sort}`;

    if (localCache[cacheName]) {
      return localCache[cacheName];
    }

    const q1 = query(itemCollection, orderBy(sortColumn, sort));
    const snapshot = await getDocs(q1);

    const data = snapshot.docs.map((docSnap) => ({
      ...(docSnap.data() as T),
      id: docSnap.id
    }));

    localCache[cacheName] = data;
    return data;
  }

  async getItem(id: string): Promise<T> {
    const path = `${this.collectionName}/${id}`;
    const refDoc = doc(this.firestore, path);
    const snap = await getDoc(refDoc);

    if (!snap.exists()) {
      throw new Error(`Item with id '${id}' not found`);
    }

    return {
      ...(snap.data() as T),
      id: snap.id,
    };
  }


  async getItemBySlug(slug: string): Promise<T | null> {
    const itemCollection = collection(this.firestore, this.collectionName);
    const myQuery = query(itemCollection, where('slug', '==', slug));
    const snapshot = await getDocs(myQuery);

    const docSnap = snapshot.docs[0];
    return docSnap ? { ...(docSnap.data() as T), id: docSnap.id } : null;
  }

  async createItem(payload: T): Promise<DocumentReference<T>> {
    const refCollection = collection(this.firestore, this.collectionName);
    localCache = {};
    return addDoc(refCollection, payload as any);
  }

  async setItem(payload: T, id: string): Promise<void> {
    const path = `${this.collectionName}/${id}`;
    const refDoc = doc(this.firestore, path);
    localCache = {};
    return setDoc(refDoc, payload as any);
  }

  async updateItem(id: string, payload: Partial<T>): Promise<void> {
    const path = `${this.collectionName}/${id}`;
    const refDoc = doc(this.firestore, path);
    localCache = {};
    return updateDoc(refDoc, payload as any);
  }

  async deleteItem(id: string): Promise<void> {
    const path = `${this.collectionName}/${id}`;
    const refDoc = doc(this.firestore, path);
    localCache = {};
    return deleteDoc(refDoc);
  }

  async uploadFile(file: Blob, filename: string) {
    const fileRef = storageRef(this.storage, `images/${filename}`);
    const metadata = { contentType: 'image/jpeg' };

    return uploadBytes(fileRef, file, metadata);
  }

  async getDownloadURL(filename: string) {
    return getDownloadURL(storageRef(this.storage, filename));
  }
}

